import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { HiMenuAlt3 } from "react-icons/hi";
import {
  Menu as MenuIcon,
  Search,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { tridotlogo } from "../../images";

const Navigation: React.FC = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleSignupOption = (role: "teacher" | "student" | "organization") => {
    navigate(`/signup/${role}`);
    toggleNav();
    setIsDropdownOpen(false);
  };

  const navigate = useNavigate();
  const location = useLocation();

  // Navigation menu items
  const navItems = [
    { label: "Home", path: "/" },
    { label: "Courses", path: "/courses" },
    { label: "About Us", path: "/about-us" },
    { label: "Contact", path: "/contact-us" },
  ];

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Toggle mobile navigation
  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
    document.body.style.overflow = isNavOpen ? "unset" : "hidden";
  };

  // Toggle search
  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  // Handle search submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Implement search logic
    }
  };

  return (
    <nav
      className={`
        fixed top-0 left-0 w-full z-50 transition-all duration-300
        ${isScrolled ? "bg-white shadow-md" : "bg-white shadow-md"}
      `}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 md:space-x-10">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={tridotlogo}
              alt="Tridot Logo"
              className="h-14 w-auto transition-transform hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-8 items-center">
            {navItems.map((item) => (
              <Link
                onClick={() => toggleNav()}
                key={item.path}
                to={item.path}
                className={`
                  text-gray-700 hover:text-indigo-600 transition-colors
                  ${
                    location.pathname === item.path
                      ? "text-indigo-600 font-semibold"
                      : ""
                  }
                `}>
                {item.label}
              </Link>
            ))}

            {/* Search */}
            <div className="relative">
              <button
                onClick={toggleSearch}
                className="text-gray-700 hover:text-indigo-600">
                <Search className="w-5 h-5" />{" "}
              </button>
              {isSearchOpen && (
                <form
                  onSubmit={handleSearchSubmit}
                  className="absolute top-full right-0 mt-2 w-64 bg-white shadow-lg rounded-lg p-2">
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="What do you want to learn?"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </form>
              )}
            </div>

            {/* Auth Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 border border-indigo-500 text-indigo-500 rounded-md hover:bg-indigo-50 transition-colors">
                Log in
              </button>
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center">
                  Sign up
                  {isDropdownOpen ? (
                    <ChevronUp className="ml-2 w-4 h-4" />
                  ) : (
                    <ChevronDown className="ml-2 w-4 h-4" />
                  )}
                </button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-lg overflow-hidden z-50">
                    <button
                      onClick={() => handleSignupOption("teacher")}
                      className="w-full text-left px-4 py-2 hover:bg-indigo-50 transition-colors">
                      as a Tutor
                    </button>
                    <button
                      onClick={() => handleSignupOption("student")}
                      className="w-full text-left px-4 py-2 hover:bg-indigo-50 transition-colors">
                      as a Student
                    </button>
                    <button
                      onClick={() => handleSignupOption("organization")}
                      className="w-full text-left px-4 py-2 hover:bg-indigo-50 transition-colors">
                      as an Organisation
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="lg:hidden">
            <button
              onClick={toggleNav}
              className="text-gray-700 focus:outline-none">
              {isNavOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <HiMenuAlt3 className="w-10 h-10  cursor-pointer" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {isNavOpen && (
        <div className="fixed inset-0 bg-black/90 z-40 lg:hidden">
          <div className="flex flex-col items-center justify-center h-full space-y-6 text-white">
            {/* Mobile Search */}
            <form onSubmit={handleSearchSubmit} className="w-[90%] max-w-md">
              <div className="relative">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="What do you want to learn?"
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </form>

            {/* Mobile Navigation Links */}
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={toggleNav}
                className={`
                  text-2xl hover:text-indigo-400 transition-colors
                  ${
                    location.pathname === item.path
                      ? "text-indigo-400 font-semibold"
                      : ""
                  }
                `}>
                {item.label}
              </Link>
            ))}

            {/* Mobile Auth Buttons */}
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => {
                  navigate("/login");
                  toggleNav();
                }}
                className="px-6 py-3 border border-indigo-500 text-indigo-500 rounded-md hover:bg-indigo-50 transition-colors">
                Log in
              </button>
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="px-4 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center">
                  Sign up
                  {isDropdownOpen ? (
                    <ChevronUp className="ml-2 w-4 h-4" />
                  ) : (
                    <ChevronDown className="ml-2 w-4 h-4" />
                  )}
                </button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-lg overflow-hidden z-50">
                    <button
                      onClick={() => handleSignupOption("teacher")}
                      className="w-full text-black text-left px-4 py-2 hover:bg-indigo-50 transition-colors">
                      as Teacher
                    </button>
                    <button
                      onClick={() => handleSignupOption("student")}
                      className="w-full text-black text-left px-4 py-2 hover:bg-indigo-50 transition-colors">
                      as Student
                    </button>
                    <button
                      onClick={() => handleSignupOption("organization")}
                      className="w-full text-black text-left px-4 py-2 hover:bg-indigo-50 transition-colors">
                      as organisation
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
